import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg, fmtDate } from "@/lib/api";
import { LoadError } from "@/admin/LoadError";
import { AdminEmail } from "@/admin/AdminEmail";

const FIELDS: [string, string][] = [["map_load_per_1000", "Map load $ / 1,000"], ["autocomplete_per_1000", "Autocomplete $ / 1,000"], ["place_details_per_1000", "Place details $ / 1,000"],
  ["autocomplete_requests_per_select", "Autocomplete requests per address (est.)"], ["free_per_sku", "Free uses per SKU / month"], ["monthly_budget_usd", "Monthly Google budget $"]];

export function AdminSettings() {
  const [p, setP] = useState<any>(null);
  const [bill, setBill] = useState({ month: new Date().toISOString().slice(0, 7), amount_usd: "", note: "" });
  const [codes, setCodes] = useState<string[] | null>(null);
  const [loadErr, setLoadErr] = useState(false);
  useEffect(() => { api.get("/admin/pricing").then((r) => setP(r.data)).catch(() => setLoadErr(true)); }, []);
  const save = async () => {
    try { await api.put("/admin/pricing", Object.fromEntries(FIELDS.map(([k]) => [k, Number(p[k])]))); toast.success("Pricing assumptions saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const saveBill = async () => {
    try { await api.put(`/admin/google-billing/${bill.month}`, { amount_usd: Number(bill.amount_usd), note: bill.note }); toast.success("Google bill recorded"); } catch (e) { toast.error(errMsg(e)); }
  };
  const regen = async () => {
    if (!window.confirm("Replace your recovery codes? Old codes stop working.")) return;
    try { setCodes((await api.post("/admin/mfa/recovery-codes")).data.recovery_codes); } catch (e) { toast.error(errMsg(e)); }
  };
  if (loadErr) return <p className="text-sm text-destructive" role="alert" data-testid="admin-load-error">Couldn’t load settings. Refresh to try again.</p>;
  if (!p) return <p className="text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-5" data-testid="admin-settings">
      <AdminEmail />
      <section className="bg-card border rounded-xl p-4 space-y-3">
        <h3 className="font-semibold">Google pricing assumptions</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.map(([k, l]) => <div key={k}><Label className="text-xs">{l}</Label><Input className="h-11" inputMode="decimal" value={p[k]} onChange={(e) => setP({ ...p, [k]: e.target.value })} data-testid={`admin-pricing-${k}`} /></div>)}
        </div>
        <Button onClick={save} data-testid="admin-pricing-save">Save assumptions</Button>
      </section>
      <section className="bg-card border rounded-xl p-4 space-y-3">
        <h3 className="font-semibold">Reconcile with Google Cloud billing</h3>
        <p className="text-xs text-muted-foreground">Enter the Maps Platform total from your Google Cloud billing report to compare it with the app’s estimate on the Overview.</p>
        <div className="grid grid-cols-2 gap-3">
          <Input className="h-11" type="month" value={bill.month} onChange={(e) => setBill({ ...bill, month: e.target.value })} data-testid="admin-bill-month" />
          <Input className="h-11" inputMode="decimal" placeholder="$ billed" value={bill.amount_usd} onChange={(e) => setBill({ ...bill, amount_usd: e.target.value })} data-testid="admin-bill-amount" />
        </div>
        <Input className="h-11" placeholder="Note (optional)" value={bill.note} onChange={(e) => setBill({ ...bill, note: e.target.value })} data-testid="admin-bill-note" />
        <Button variant="outline" disabled={!bill.amount_usd} onClick={saveBill} data-testid="admin-bill-save">Record Google bill</Button>
      </section>
      <section className="bg-card border rounded-xl p-4 space-y-3">
        <h3 className="font-semibold">Two-step verification</h3>
        <p className="text-xs text-muted-foreground">Admin access needs your authenticator code every 12 hours.</p>
        {codes ? <ul className="grid grid-cols-2 gap-2 font-mono text-sm bg-secondary rounded-lg p-3" data-testid="admin-new-recovery-codes">{codes.map((c) => <li key={c}>{c}</li>)}</ul>
          : <Button variant="outline" onClick={regen} data-testid="admin-regenerate-codes">Generate new recovery codes</Button>}
      </section>
    </div>
  );
}

export function AdminAudit() {
  const [items, setItems] = useState<any[] | null>(null);
  const [err, setErr] = useState("");
  const load = () => { setErr(""); setItems(null); api.get("/admin/audit").then((r) => setItems(r.data.items)).catch((e) => setErr(errMsg(e))); };
  useEffect(() => { load(); }, []);
  if (err) return <LoadError what="the audit log" detail={err} onRetry={load} testid="admin-audit-error" />;
  if (!items) return <p className="text-muted-foreground">Loading…</p>;
  return (
    <ul className="space-y-2" data-testid="admin-audit-list">
      {items.length === 0 && <li className="text-sm text-muted-foreground">No admin activity yet.</li>}
      {items.map((a) => (
        <li key={a.id} className="bg-card border rounded-lg p-3 text-xs">
          <p><b>{a.action}</b> · {fmtDate(a.at)} · {a.admin_email}</p>
          {a.org_id && <p className="text-muted-foreground">Company {a.org_id.slice(0, 8)}</p>}
          {a.details && Object.keys(a.details).length > 0 && <p className="text-muted-foreground break-all">{JSON.stringify(a.details)}</p>}
        </li>
      ))}
    </ul>
  );
}
